import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Heart, Zap, FileText, BarChart3, ChevronRight, Activity } from 'lucide-react';

const monitors = [
  {
    title: 'Saúde Financeira',
    desc: 'Pergunte pelo fluxo de caixa ou pela margem do período e receba a resposta consultada ao vivo, direto do seu banco.',
    icon: <BarChart3 size={20}/>,
    status: 'Estável',
    color: 'emerald'
  },
  {
    title: 'Compliance Fiscal',
    desc: 'Pergunte sobre notas e obrigações acessórias pendentes e o LukIA responde na hora, sem esperar relatório pronto.',
    icon: <FileText size={20}/>,
    status: 'Auditado',
    color: 'blue'
  },
  {
    title: 'Pistas & PDVs',
    desc: 'Pergunte pelo status de terminais e bombas e veja o retrato atual da operação em segundos.',
    icon: <Zap size={20}/>,
    status: 'Online',
    color: 'amber'
  }
];

export default function IAHealthMonitor() {
  return (
    <section className="py-24 bg-slate-50 relative overflow-hidden font-sans border-y border-slate-100">
      <div className="container mx-auto px-6 max-w-6xl relative z-10">
        <div className="flex flex-col items-center text-center mb-20 max-w-2xl mx-auto">
           <motion.div 
             initial={{ opacity: 0, scale: 0.8 }}
             whileInView={{ opacity: 1, scale: 1 }}
             viewport={{ once: true }}
             className="w-16 h-16 rounded-[1.5rem] bg-white shadow-xl flex items-center justify-center text-rose-500 mb-8 relative"
           >
              {/* Pulse Animation */}
              <motion.div 
                animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0, 0.3] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute inset-0 bg-rose-400 rounded-[1.5rem]"
              />
              <Heart size={28} className="relative z-10 fill-rose-500" />
           </motion.div>
           
           <h2 className="text-4xl lg:text-5xl font-black text-slate-900 tracking-tight leading-[1.1] mb-6">
             Pergunte. Responde na hora. <br/>
             <span className="text-slate-400 font-light italic">O pulso da sua operação, sob demanda.</span>
           </h2>
           <p className="text-slate-500 text-lg font-medium leading-relaxed">
             Sem esperar relatório pronto: pergunte em português sobre financeiro, fiscal ou pistas, e o LukIA consulta os dados do seu posto na hora, com acesso somente leitura e validado.
           </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
           {monitors.map((item, i) => (
             <motion.div
               key={i}
               initial={{ opacity: 0, y: 20 }}
               whileInView={{ opacity: 1, y: 0 }}
               viewport={{ once: true }}
               transition={{ delay: i * 0.15 }}
               className="bg-white p-8 rounded-[2.5rem] border border-slate-200/60 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.05)] hover:shadow-[0_30px_60px_-20px_rgba(0,0,0,0.1)] transition-all duration-500 group"
             >
                <div className="flex items-center justify-between mb-8">
                   <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-800 transition-colors group-hover:bg-slate-900 group-hover:text-white">
                      {item.icon}
                   </div>
                   <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      <span className="text-[10px] font-black uppercase tracking-widest">{item.status}</span>
                   </div>
                </div>

                <h3 className="text-xl font-black text-slate-900 mb-4 tracking-tight">{item.title}</h3>
                <p className="text-slate-500 text-sm font-medium leading-relaxed mb-8">
                   {item.desc}
                </p>

                <div className="h-2 w-full bg-slate-50 rounded-full overflow-hidden mb-6">
                   <motion.div 
                     initial={{ width: 0 }}
                     whileInView={{ width: '100%' }}
                     transition={{ duration: 1.5, delay: 0.5 + (i * 0.2) }}
                     className="h-full bg-emerald-500"
                   />
                </div>

                <button className="flex items-center gap-2 group text-slate-900 font-black text-[10px] uppercase tracking-widest">
                   Abrir Relatório <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </button>
             </motion.div>
           ))}
        </div>

        {/* Dynamic Wave Visualization */}
        <div className="mt-20 flex items-center justify-center gap-1.5 h-12">
           {[...Array(20)].map((_, i) => (
             <motion.div 
               key={i}
               animate={{ height: [10, 40, 10, 25, 10] }}
               transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.1, ease: "easeInOut" }}
               className="w-1.5 bg-slate-200 rounded-full opacity-50"
             />
           ))}
        </div>
      </div>
    </section>
  );
}
